import {
  Button,
  SimpleModalHeader,
  SimpleModalHeaderIcon,
  SimpleModalTitle,
} from '@fountain/fountain-ui-components';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import CustomModal from 'components/CustomModal';
import messages from 'components/PartnerDetailsModal/messages';
import InfoBlueIcon from 'images/info-blue.svg';

import useStyles from './styles';

function PartnerDetail({ details, requestedRevision, validity }) {
  const [isEmbedOpen, setIsEmbedOpen] = useState(false);
  const classes = useStyles();

  if (!details.length) return null;

  const partnerOption = details[0].partner_option;

  // A state this component does not know renders nothing. Falling back to the
  // reassuring sentence would tell a recruiter a check is in force on a value
  // nobody here has read, and this display is the one that says otherwise.
  const validityReading = {
    valid: {
      // A reading that predates the counter carries no number, and the day alone is all it says.
      message:
        typeof validity?.days_remaining === 'number'
          ? messages.expiresOnWithDays
          : messages.expiresOn,
      className: classes.validity,
    },
    expired: {
      message: messages.expiredOn,
      className: classes.validityExpired,
    },
  }[validity?.state];

  const resultDate = detail =>
    detail.result_date ? (
      <Typography variant="body2" className={classes.detailMeta}>
        {detail.result_date}
      </Typography>
    ) : null;

  const partnerDetail = detail => {
    switch (detail.category) {
      case 'url':
      case 'file': {
        const link = (
          <a href={detail.value} target="_blank" rel="noreferrer">
            {detail.label}
          </a>
        );
        if (!detail.result_date) return link;
        return (
          <Grid container>
            {link}
            {resultDate(detail)}
          </Grid>
        );
      }
      case 'embed_url':
        return (
          <Grid container>
            <Button
              variant="contained"
              onClick={() => setIsEmbedOpen(true)}
              size="small"
              type="primary"
              style={{ width: 'auto' }}
            >
              {detail.label}
            </Button>
            <CustomModal
              isOpen={isEmbedOpen}
              handleClose={() => setIsEmbedOpen(false)}
              whiteBackground
              fullScreen
              noPadding
              fullHeight
              hideCloseButton
              classes={{
                root: classes.modalRoot,
                paperFullScreen: classes.modalContent,
                paper: classes.iframeModal,
                topSection: classes.topSection,
              }}
              customHeaderContent={
                <SimpleModalHeader onClose={() => setIsEmbedOpen(false)}>
                  <SimpleModalHeaderIcon Icon={InfoBlueIcon} color="blue" />
                  <SimpleModalTitle>{detail.label}</SimpleModalTitle>
                </SimpleModalHeader>
              }
            >
              <iframe
                className={classes.iframe}
                src={detail.value}
                title="embed"
                allow="camera; microphone; geolocation"
              />
            </CustomModal>
            {resultDate(detail)}
          </Grid>
        );
      default:
        return (
          <Grid container>
            <Typography variant="body2" className={classes.detailLabel}>
              {detail.label}:
            </Typography>
            <Typography variant="body2" className={classes.detailValue}>
              &nbsp;{detail.value}
            </Typography>
            {resultDate(detail)}
          </Grid>
        );
    }
  };

  return (
    <Grid className={classes.partnerDetailContainer}>
      <Typography
        variant="body1"
        className={classes.partnerOption}
        color="textPrimary"
      >
        {partnerOption} {requestedRevision && ' - Requested Revision'}
      </Typography>
      {validity?.missing_rehire && (
        <Typography variant="body2" className={classes.missingRehire}>
          <FormattedMessage {...messages.missingRehire} />
        </Typography>
      )}
      {validityReading && (
        <Typography variant="body2" className={validityReading.className}>
          <FormattedMessage
            {...validityReading.message}
            values={{
              date: validity.date,
              daysRemaining: validity.days_remaining,
            }}
          />
        </Typography>
      )}
      {validityReading && validity.reused_from && (
        <Typography variant="body2" className={classes.validity}>
          <FormattedMessage {...messages.reusedFrom} />
        </Typography>
      )}
      <Grid className={classes.partnerDetails}>
        {details.map(detail => (
          <Grid
            key={detail.external_id || detail.key}
            className={classes.partnerDetail}
          >
            {partnerDetail(detail)}
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}

PartnerDetail.propTypes = {
  details: PropTypes.array,
  requestedRevision: PropTypes.bool,
  // Both fields are resolved by the backend, so the state is read and never
  // derived here. Absent is the state where no check ever passed.
  validity: PropTypes.shape({
    state: PropTypes.oneOf(['valid', 'expired']).isRequired,
    date: PropTypes.string.isRequired,
    days_remaining: PropTypes.number, // eslint-disable-line camelcase
    missing_rehire: PropTypes.bool, // eslint-disable-line camelcase
    reused_from: PropTypes.string, // eslint-disable-line camelcase
  }),
};

export default PartnerDetail;
